import { dlog } from '../utils/debug';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue, remove, query, orderByChild, equalTo, get, set, update } from 'firebase/database';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getContrastTextColor } from '../utils/contrast';
import { getMatchStatus } from '../utils/matchCompletion';
import { inferMyTeam, getMostUsedColor } from '../utils/emotionEngine';
import SetDisplay from '../components/SetDisplay';
import { DynamicFlapperLogo } from '../components';
import Drawer from '../components/Drawer';
import MatchGroup from '../components/MatchGroup';
import ShareGroupModal from '../components/ShareGroupModal';
import SignInSheet from '../components/SignInSheet';
import GeoBanner from '../components/GeoBanner';
import { groupMatchesByDate } from '../utils/matchGrouping';
import { groupMatchesWithSharedLabels } from '../utils/groupMatchesWithSharedLabels';
import { Check, Trash, Sparkle, Coffee } from 'phosphor-react';
import useSubscription from '../hooks/useSubscription';
import FEATURES, { isFeatureEnabled } from '../config/features';
import { SUPPORT_URL } from '../config/support';
import FeatureGate from '../components/FeatureGate';

import '../styles/LandingPage.css';

function LandingPage() {
  const location = useLocation();
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  // Swipe-to-delete state.
  //   swipeOpenId — which row is locked in revealed-delete state.
  // Drag motion is driven via direct DOM writes (dragRef) to avoid a
  // React re-render on every touchmove, which was causing visible jank.
  const [swipeOpenId, setSwipeOpenId] = useState(null);
  const dragRef = useRef(null); // { id, startX, rowWidth, startOffset, currentOffset, rowEl, hasMoved }
  const REVEAL_WIDTH = 90; // px the row stops at when revealed
  const SNAP_THRESHOLD = 45; // px before the row snaps open on release
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [customLabels, setCustomLabels] = useState({});
  const [sharedGroupLabels, setSharedGroupLabels] = useState({}); // { dateKey: label } from shared groups
  const [groupCollapseStates, setGroupCollapseStates] = useState({});
  const [hasGuestMatches, setHasGuestMatches] = useState(false);
  const [shareGroupModal, setShareGroupModal] = useState(null); // { groupId, label, matches }
  const [migrationTrigger, setMigrationTrigger] = useState(0);
  const matchListenersRef = useRef([]);
  
  const navigate = useNavigate();
  const { user, loading, signInWithGoogle, signInWithEmail, signUpWithEmail, signOut, resetPassword, ensureAnonymousUser } = useAuth();
  const toast = useToast();
  const { isPro, getMatchLimit } = useSubscription(user);
  const paymentsEnabled = isFeatureEnabled('PAYMENTS', user);
  const hasFullAccess = !paymentsEnabled || isPro;
  // Entry-point rework: when on, non-auth visitors see a compact "Start a
  // match" surface instead of the full auth wall. Tapping Start triggers
  // ensureAnonymousUser() so they can score without signing up. Both
  // flags must be enabled — anonymous scoring is the mechanism, entry-
  // point rework is the UI change.
  const entryPointReworkEnabled =
    isFeatureEnabled('ENTRY_POINT_REWORK', user) &&
    isFeatureEnabled('ANONYMOUS_SCORING', user);
  // Toggles the legacy auth form below the new compact zero-state.
  const [showAuthForm, setShowAuthForm] = useState(false);
  // Guard the auto-redirect-to-new-match effect so it fires at most
  // once per browser session — not just once per mount. Without the
  // session-level guard, an anon user who deletes their only match
  // would land back on the landing with 0 matches and immediately
  // get redirected into a fresh editor, looping the deletion.
  const hasAutoRedirectedRef = useRef(
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem('autoRedirectFired') === '1'
  );
  // Tracks which user UID we've completed a match-load cycle for.
  // The redirect effect uses this to avoid the race where
  // matchesLoading was last set to false for a *different* (or null)
  // user — without this, a refresh as an anon user would trigger a
  // new-match redirect before the matches-load effect re-fires.
  const [matchesLoadedForUid, setMatchesLoadedForUid] = useState(null);
  // Same idea for orphan cleanup — run once per mount, not on every
  // matches[] update (the deletion itself would trigger more updates).
  const hasCleanedOrphansRef = useRef(false);
  // Save-prompt sheet for the anon-matches banner. Dismissals are
  // remembered in sessionStorage so the banner doesn't pester within
  // a session. Cleared on real sign-in (user is no longer anonymous).
  const [showSavePromptSheet, setShowSavePromptSheet] = useState(false);
  const [savePromptDismissed, setSavePromptDismissed] = useState(
    () => sessionStorage.getItem('savePromptDismissed') === '1'
  );
  const savePromptsEnabled = isFeatureEnabled('SAVE_PROMPTS', user);
  const showSavePromptBanner =
    savePromptsEnabled &&
    user?.isAnonymous &&
    matches.length >= 2 &&
    !savePromptDismissed;
  // Geo banner is shown to everyone (anon + real users). It self-gates
  // on permission state — the component handles the "denied/dismissed/
  // empty" cases internally and renders null when there's nothing to
  // show.
  const geoBannerEnabled = isFeatureEnabled('GEO_BANNER', user);

  // Check for Stripe checkout redirect parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    if (params.get('upgrade') === 'success') {
      toast.success('Subscription successful — welcome to Pro!', 6000);
      // Clean up URL
      navigate('/', { replace: true });
    } else if (params.get('canceled') === 'true') {
      // User canceled - no action needed, they'll be back on upgrade page
      dlog('Payment was canceled by user');
    }
  }, [location, navigate]);

  // Check for guest matches when not logged in
  useEffect(() => {
    if (!user) {
      try {
        const guestMatches = JSON.parse(localStorage.getItem('guestViewedMatches') || '{}');
        const matchIds = Object.keys(guestMatches);
        setHasGuestMatches(matchIds.length > 0);
      } catch (error) {
        console.error('Error checking guest matches:', error);
        setHasGuestMatches(false);
      }
    } else {
      setHasGuestMatches(false);
    }
  }, [user]);

  // Get guest match count for display
  const getGuestMatchCount = () => {
    if (!user) {
      try {
        const guestMatches = JSON.parse(localStorage.getItem('guestViewedMatches') || '{}');
        return Object.keys(guestMatches).length;
      } catch (error) {
        return 0;
      }
    }
    return 0;
  };

  // Team detection function (same logic as MatchEditor)
  const checkForFavoriteTeam = async () => {
    if (!user) return;

    try {
      // Get current settings
      const settingsRef = ref(db, `users/${user.uid}/settings`);
      const settingsSnapshot = await get(settingsRef);
      const currentSettings = settingsSnapshot.val() || {};

      // Get all user's matches
      const matchesRef = ref(db, `users/${user.uid}/matches`);
      const matchesSnapshot = await get(matchesRef);
      
      if (!matchesSnapshot.exists()) {
        dlog('🔄 No matches found, resetting to defaults');
        await update(settingsRef, {
          defaultTeamName: 'Team A',
          defaultTeamColor: '#AE1F24',
          favoriteTeamDetected: false,
          favoriteTeamDetectedAt: null,
          favoriteTeamUsageCount: 0
        });
        return;
      }

      const matches = Object.entries(matchesSnapshot.val()).map(([id, data]) => ({
        id,
        ...data
      }));

      // Use settings-specific threshold (more conservative than logo)
      const myTeam = inferMyTeam(matches, 'settings');
      const mostUsedColor = getMostUsedColor(matches, myTeam);
      
      dlog('🎯 Settings team detection after deletion:', { myTeam, mostUsedColor });
      
      if (myTeam && mostUsedColor && myTeam !== 'Unknown') {
        // Find the original team name (not normalized) that matches myTeam
        let originalTeamName = myTeam; // fallback to normalized name
        
        // Look for the most recent match with this team to get the original name
        for (const match of matches.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))) {
          const teamAName = typeof match.teamA === 'object' ? match.teamA.name || match.teamA.teamName : match.teamA;
          const teamBName = typeof match.teamB === 'object' ? match.teamB.name || match.teamB.teamName : match.teamB;
          
          // Helper to normalize team names (same logic as emotionEngine)
          const normalizeTeamName = (teamName) => {
            if (!teamName) return teamName;
            return teamName
              .replace(/\s+\d+\s+\w+$/i, '') // Remove "16 Black", "2024 Gold", etc.
              .replace(/\s+\d+$/i, '') // Remove trailing numbers like "2024"
              .trim();
          };
          
          if (teamAName && normalizeTeamName(teamAName) === myTeam) {
            originalTeamName = teamAName;
            break;
          }
          if (teamBName && normalizeTeamName(teamBName) === myTeam) {
            originalTeamName = teamBName;
            break;
          }
        }
        
        const currentDefaultTeam = `${currentSettings.defaultTeamName}|${currentSettings.defaultTeamColor}`;
        const detectedTeamKey = `${originalTeamName}|${mostUsedColor}`;
        
        // Only update if it's different from current default
        if (currentDefaultTeam !== detectedTeamKey) {
          await update(settingsRef, {
            defaultTeamName: originalTeamName, // Use original name, not normalized
            defaultTeamColor: mostUsedColor,
            favoriteTeamDetected: true,
            favoriteTeamDetectedAt: Date.now(),
            favoriteTeamUsageCount: matches.filter(m => {
              const teamAName = typeof m.teamA === 'object' ? m.teamA.name || m.teamA.teamName : m.teamA;
              const teamBName = typeof m.teamB === 'object' ? m.teamB.name || m.teamB.teamName : m.teamB;
              return teamAName === originalTeamName || teamBName === originalTeamName;
            }).length
          });
          
          dlog('✅ Updated default team settings after deletion:', { originalTeamName, mostUsedColor });
          
          // Dispatch event to notify other components
          window.dispatchEvent(new CustomEvent('favoriteTeamDetected', {
            detail: { name: originalTeamName, color: mostUsedColor }
          }));
        }
      } else {
        // No clear favorite team detected - reset to defaults
        dlog('🔄 No clear favorite team detected after deletion, resetting to defaults');
        
        await update(settingsRef, {
          defaultTeamName: 'Team A',
          defaultTeamColor: '#AE1F24',
          favoriteTeamDetected: false,
          favoriteTeamDetectedAt: null,
          favoriteTeamUsageCount: 0
        });
        
        dlog('✅ Reset default team to defaults after deletion');
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('favoriteTeamDetected', {
          detail: null // null indicates reset to defaults
        }));
      }
    } catch (error) {
      console.error('❌ Error detecting favorite team after deletion:', error);
    }
  };

  // Load custom group labels from Firebase
  useEffect(() => {
    if (!user) {
      setCustomLabels({});
      return;
    }

    const labelsRef = ref(db, `users/${user.uid}/matchGroupLabels`);
    const unsubscribe = onValue(labelsRef, (snapshot) => {
      if (snapshot.exists()) {
        setCustomLabels(snapshot.val());
      } else {
        setCustomLabels({});
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Load shared group labels from Firebase
  useEffect(() => {
    if (!user) {
      // Check localStorage for guest
      try {
        const guestLabels = JSON.parse(localStorage.getItem('guestSharedGroupLabels') || '{}');
        setSharedGroupLabels(guestLabels);
      } catch (err) {
        setSharedGroupLabels({});
      }
      return;
    }

    const sharedLabelsRef = ref(db, `users/${user.uid}/sharedGroupLabels`);
    const unsubscribe = onValue(sharedLabelsRef, (snapshot) => {
      if (snapshot.exists()) {
        setSharedGroupLabels(snapshot.val());
      } else {
        setSharedGroupLabels({});
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Load group collapse states from Firebase
  useEffect(() => {
    if (!user) {
      setGroupCollapseStates({});
      return;
    }

    const collapseRef = ref(db, `users/${user.uid}/groupCollapseStates`);
    const unsubscribe = onValue(collapseRef, (snapshot) => {
      if (snapshot.exists()) {
        setGroupCollapseStates(snapshot.val());
      } else {
        setGroupCollapseStates({});
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Handle custom label changes
  const handleLabelChange = async (dateKey, newLabel) => {
    if (!user) return;

    const updatedLabels = {
      ...customLabels,
      [dateKey]: newLabel
    };
    setCustomLabels(updatedLabels);

    // Save to Firebase
    try {
      const labelsRef = ref(db, `users/${user.uid}/matchGroupLabels/${dateKey}`);
      await set(labelsRef, newLabel);
    } catch (error) {
      console.error('Error saving custom label:', error);
    }
  };

  // Handle collapse state changes
  const handleCollapseStateChange = async (dateKey, isExpanded) => {
    if (!user) return;

    const updatedStates = {
      ...groupCollapseStates,
      [dateKey]: isExpanded
    };
    setGroupCollapseStates(updatedStates);

    // Save to Firebase
    try {
      const collapseRef = ref(db, `users/${user.uid}/groupCollapseStates/${dateKey}`);
      await set(collapseRef, isExpanded);
    } catch (error) {
      console.error('Error saving collapse state:', error);
    }
  };

  // Handle group sharing
  const handleShareGroup = async (dateKey, matchesToShare, groupLabel) => {
    if (!user) {
      toast.error('Please sign in to share groups.');
      return;
    }
    
    if (!isFeatureEnabled('SHARE_GROUPS', user)) {
      toast.error('Group sharing is not available.');
      return;
    }

    if (!matchesToShare || matchesToShare.length === 0) {
      toast.warning('No matches to share.');
      return;
    }

    try {
      // Generate unique group ID
      const groupId = `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Extract match IDs safely
      const matchIds = matchesToShare
        .map(m => m?.id || m?.matchId)
        .filter(id => id != null);
      
      if (matchIds.length === 0) {
        toast.error('No valid match IDs found.');
        return;
      }
      
      dlog('Creating shared group:', { groupId, dateKey, label: groupLabel, matchIds });
      
      // Create shared group in Firebase
      const sharedGroupRef = ref(db, `sharedGroups/${groupId}`);
      const groupData = {
        creatorId: user.uid,
        createdAt: Date.now(),
        dateKey,
        label: groupLabel,
        matchIds
      };
      
      dlog('Group data to save:', groupData);
      
      await set(sharedGroupRef, groupData);

      dlog('Group created successfully:', groupId);

      // Open share modal
      setShareGroupModal({
        groupId,
        label: groupLabel,
        matchCount: matchesToShare.length
      });
    } catch (error) {
      console.error('Error sharing group:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      let errorMessage = 'Unable to share group. Please try again.';
      if (error.code === 'PERMISSION_DENIED') {
        errorMessage = 'Permission denied. Database rules may need to be updated.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      toast.error(errorMessage, 6000);
    }
  };

  // Handle group deletion
  const handleDeleteGroup = async (dateKey, matchesToDelete) => {
    if (!user) return;

    try {
      // Delete all matches in the group
      for (const match of matchesToDelete) {
        const matchRef = ref(db, `matches/${match.id}`);
        await remove(matchRef);
      }

      // Delete custom label if it exists
      if (customLabels[dateKey]) {
        const labelRef = ref(db, `users/${user.uid}/matchGroupLabels/${dateKey}`);
        await remove(labelRef);
      }

      // Delete collapse state if it exists
      if (groupCollapseStates[dateKey] !== undefined) {
        const collapseRef = ref(db, `users/${user.uid}/groupCollapseStates/${dateKey}`);
        await remove(collapseRef);
      }
      
      // Check for favorite team after group deletion
      await checkForFavoriteTeam();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group. Please try again.');
    }
  };



  // 🔹 Load user's matches only if signed in
  useEffect(() => {
    if (!user) {
      setMatches([]);
      setMatchesLoading(false);
      setMatchesLoadedForUid(null);
      return;
    }

    // Reset the "loaded for this uid" marker so the redirect effect
    // waits for the new user's load cycle to complete before deciding.
    setMatchesLoadedForUid(null);

    // Load both created matches AND viewed matches
    const loadAllMatches = async () => {
      setMatchesLoading(true);
      
      try {
        // 1. Get matches created by user
        const matchesRef = ref(db, 'matches');
        const createdQuery = query(matchesRef, orderByChild('creatorId'), equalTo(user.uid));
        const createdSnapshot = await get(createdQuery);
        
        const createdMatches = new Map();
        if (createdSnapshot.exists()) {
          Object.entries(createdSnapshot.val()).forEach(([id, match]) => {
            createdMatches.set(id, { id, ...match, isOwner: true });
          });
          dlog('✅ Loaded', createdMatches.size, 'created matches');
        }

        // 2. Get user's viewed matches list (includes shared group matches)
        const viewedRef = ref(db, `users/${user.uid}/viewedMatches`);
        const viewedSnapshot = await get(viewedRef);
        
        if (viewedSnapshot.exists()) {
          const viewedMatchesData = viewedSnapshot.val();
          const viewedMatchIds = Object.keys(viewedMatchesData);
          dlog('✅ Found', viewedMatchIds.length, 'viewed match IDs:', viewedMatchIds);
          
          // 3. Fetch each viewed match
          for (const matchId of viewedMatchIds) {
            if (!createdMatches.has(matchId)) {
              const matchRef = ref(db, `matches/${matchId}`);
              const matchSnapshot = await get(matchRef);
              
              if (matchSnapshot.exists()) {
                const viewedData = viewedMatchesData[matchId] || {};
                createdMatches.set(matchId, { 
                  id: matchId, 
                  ...matchSnapshot.val(), 
                  isOwner: false,
                  fromSharedGroup: viewedData.fromSharedGroup || null // Track which shared group this came from
                });
                dlog('✅ Loaded shared match:', matchId);
              } else {
                console.warn('⚠️ Viewed match not found:', matchId);
              }
            }
          }
        } else {
          dlog('ℹ️ No viewed matches found');
        }

        // 4. Convert to array and sort by creation date (most recent first)
        const allMatches = Array.from(createdMatches.values())
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        
        dlog('✅ Total matches loaded:', allMatches.length);
        setMatches(allMatches);
      } catch (error) {
        console.error('❌ Error loading matches:', error);
        setMatches([]);
      } finally {
        setMatchesLoading(false);
        setMatchesLoadedForUid(user.uid);
      }
    };

    loadAllMatches();
    
    // Set up real-time listener for created matches
    const matchesRef = ref(db, 'matches');
    const createdQuery = query(matchesRef, orderByChild('creatorId'), equalTo(user.uid));
    const unsubscribeCreated = onValue(createdQuery, () => {
      loadAllMatches(); // Reload all when created matches change
    });

    // Set up real-time listeners for viewed matches
    const viewedRef = ref(db, `users/${user.uid}/viewedMatches`);
    const unsubscribeViewed = onValue(viewedRef, (snapshot) => {
      // Clean up existing match listeners
      matchListenersRef.current.forEach(unsubscribe => unsubscribe());
      matchListenersRef.current = [];
      
      if (snapshot.exists()) {
        const viewedMatchIds = Object.keys(snapshot.val());
        dlog('🔄 Viewed matches changed, setting up real-time listeners for:', viewedMatchIds);
        
        // Set up individual listeners for each viewed match
        viewedMatchIds.forEach(matchId => {
          const matchRef = ref(db, `matches/${matchId}`);
          const unsubscribeMatch = onValue(matchRef, (matchSnapshot) => {
            if (matchSnapshot.exists()) {
              dlog('🔄 Viewed match updated:', matchId);
              // Update the specific match in our state
              setMatches(prevMatches => {
                const updatedMatches = prevMatches.map(match => 
                  match.id === matchId 
                    ? { ...match, ...matchSnapshot.val() }
                    : match
                );
                return updatedMatches;
              });
            }
          });
          matchListenersRef.current.push(unsubscribeMatch);
        });
      }
    });

    return () => {
      unsubscribeCreated();
      unsubscribeViewed();
      // Clean up all match listeners
      matchListenersRef.current.forEach(unsubscribe => unsubscribe());
      matchListenersRef.current = [];
    };
  }, [user, migrationTrigger]);

  // Listen for guest matches migration completion
  useEffect(() => {
    if (!user) return;

    const handleMigrationComplete = () => {
      dlog('🔄 Guest matches migration complete, reloading matches...');
      setMigrationTrigger(prev => prev + 1);
    };

    window.addEventListener('guestMatchesMigrated', handleMigrationComplete);
    
    return () => {
      window.removeEventListener('guestMatchesMigrated', handleMigrationComplete);
    };
  }, [user]);

  // Filter matches based on subscription (when PAYMENTS feature is enabled)
  const visibleMatches = useMemo(() => {
    if (hasFullAccess) {
      return matches;
    }

    // Free tier: Last 3 matches only
    const limit = getMatchLimit();
    if (!limit || limit.count === Infinity) return matches; // No limit
    
    return matches.slice(0, limit.count);
  }, [matches, hasFullAccess, getMatchLimit]);

  // Calculate hidden matches count
  const hiddenMatchesCount = matches.length - visibleMatches.length;

  const handleCreateMatch = async () => {
    // Explicit "Start a match" tap — clear the intentional-sign-out
    // marker so the auto-redirect path can fire again on subsequent
    // visits. (The user has decided to score anonymously; that's a
    // fresh intent, distinct from the moment they signed out.)
    sessionStorage.removeItem('wasSignedOut');

    // Non-auth visitors: lazy-create an anonymous Firebase user so the
    // match has a stable creatorId for RTDB. Only fires when the
    // entry-point rework + anonymous scoring flags are both on; without
    // them, fall back to the legacy "must be signed in" behavior.
    if (!user) {
      if (!entryPointReworkEnabled) return;
      const anon = await ensureAnonymousUser();
      if (!anon) {
        toast.error('Couldn’t start a match. Please try again.');
        return;
      }
    }

    // Gate feature if payments enabled for this user and user is not Pro
    if (paymentsEnabled && !isPro) {
      navigate('/upgrade');
      return;
    }

    const matchId = Math.random().toString(36).substring(2, 10);

    // Navigate directly to editor - creatorId will be set when match is saved
    navigate(`/match/editor/${matchId}`);
  };

  // Entry-point rework: auto-redirect anon visitors (and not-yet-signed-
  // in visitors) straight to a fresh scoring surface when they have no
  // matches. Replaces the "land on a CTA" experience with "land on the
  // product." Fires once per mount. Suppressed when the user just
  // intentionally signed out — otherwise "Sign Out" loops into
  // "scored a new match."
  useEffect(() => {
    if (!entryPointReworkEnabled) return;
    if (hasAutoRedirectedRef.current) return;
    if (loading) return;
    if (sessionStorage.getItem('wasSignedOut') === '1') return;

    // Not signed in at all → straight into a new match (handleCreateMatch
    // calls ensureAnonymousUser internally). No need to wait for matches
    // to load — there's no user to load matches for.
    if (!user) {
      hasAutoRedirectedRef.current = true;
      try { sessionStorage.setItem('autoRedirectFired', '1'); } catch {}
      handleCreateMatch();
      return;
    }

    // Anonymous with no matches → straight into a new match. But ONLY
    // after the matches-load cycle for this specific user UID has
    // completed; otherwise on refresh we'd race a stale matches=[] and
    // create a new match even though saved ones exist.
    if (user.isAnonymous) {
      if (matchesLoading) return;
      if (matchesLoadedForUid !== user.uid) return;
      if (matches.length === 0) {
        hasAutoRedirectedRef.current = true;
        try { sessionStorage.setItem('autoRedirectFired', '1'); } catch {}
        handleCreateMatch();
      }
    }

    // Real signed-in users: never auto-redirected.
  }, [user, loading, matchesLoading, matchesLoadedForUid, matches.length, entryPointReworkEnabled]);

  // Orphan cleanup: anonymous users who poked the app, created a draft
  // match, then left would otherwise pile empty matches in RTDB. On
  // each anon-user landing, scan their matches and silently delete any
  // with no scoring activity older than 30 minutes. Only applies to
  // anon-owned matches — real users manage their own data.
  useEffect(() => {
    if (!entryPointReworkEnabled) return;
    if (hasCleanedOrphansRef.current) return;
    if (loading || matchesLoading) return;
    if (!user?.isAnonymous) return;
    if (matches.length === 0) return;

    const ORPHAN_AGE_MS = 30 * 60 * 1000;
    const now = Date.now();
    const orphans = matches.filter((m) => {
      const noPoints = (m.score?.teamA ?? 0) === 0 && (m.score?.teamB ?? 0) === 0;
      const noSetsWon = (m.setsWon?.teamA ?? 0) === 0 && (m.setsWon?.teamB ?? 0) === 0;
      const allSetsEmpty = !Array.isArray(m.sets) ||
        m.sets.every((s) => (s?.teamA ?? 0) === 0 && (s?.teamB ?? 0) === 0);
      const aged = now - (m.createdAt || 0) > ORPHAN_AGE_MS;
      return noPoints && noSetsWon && allSetsEmpty && aged;
    });

    if (orphans.length === 0) return;
    hasCleanedOrphansRef.current = true;
    dlog(`🧹 Cleaning ${orphans.length} orphaned anon match(es)`);
    // Fire-and-forget; failures are non-blocking and silent.
    Promise.all(
      orphans.map((m) => remove(ref(db, `matches/${m.id}`)).catch(() => {}))
    );
  }, [user, loading, matchesLoading, matches, entryPointReworkEnabled]);

  const handleDelete = async (e, matchId, isOwner) => {
    e.stopPropagation();
    
    if (isOwner) {
      // Creator: Delete the match entirely
      const confirmDelete = window.confirm('Are you sure you want to delete this match? This will remove it for everyone.');
      if (confirmDelete) {
        try {
          await remove(ref(db, `matches/${matchId}`));
          // Also remove from viewed matches if present
          await remove(ref(db, `users/${user.uid}/viewedMatches/${matchId}`));
          
          // Check for favorite team after deletion
          await checkForFavoriteTeam();
        } catch (error) {
          console.error('Error deleting match:', error);
        }
      }
    } else {
      // Viewer: Just remove from their viewed list
      const confirmRemove = window.confirm('Remove this match from your list?');
      if (confirmRemove) {
        try {
          await remove(ref(db, `users/${user.uid}/viewedMatches/${matchId}`));
          // Manually update the local state to reflect the removal
          setMatches(matches.filter(m => m.id !== matchId));
          
          // Check for favorite team after removal
          await checkForFavoriteTeam();
        } catch (error) {
          console.error('Error removing match:', error);
        }
      }
    }
  };

  // 🔹 Auth handlers
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    const result = await signInWithGoogle();
    setIsLoading(false);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }

    if (isSignUp && !displayName) {
      return;
    }

    setIsLoading(true);
    
    let result;
    if (isSignUp) {
      result = await signUpWithEmail(email, password, displayName);
    } else {
      result = await signInWithEmail(email, password);
    }
    
    setIsLoading(false);

    if (result.success) {
      setEmail('');
      setPassword('');
      setDisplayName('');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      return;
    }

    setIsLoading(true);
    const result = await resetPassword(resetEmail);
    setIsLoading(false);

    if (result.success) {
      setShowPasswordReset(false);
      setResetEmail('');
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  // Was the user just intentionally signed out? Set by AuthContext.
  // signOut() into sessionStorage. Suppresses the auto-redirect-
  // into-new-match flow so "Sign Out" doesn't immediately reclaim
  // the visitor as anonymous + score a new match. Cleared when they
  // sign back in via finalizeSignIn(). Re-read on each render so we
  // pick up changes without coordinating extra state.
  const wasSignedOut =
    typeof window !== 'undefined' &&
    sessionStorage.getItem('wasSignedOut') === '1';

  // 🔹 Booting states — instead of full-screen overlays with a separate
  // static loader (the old pattern produced "two flappers loading at
  // once" with the header logo, which the user flagged as inconsistent),
  // we render the regular page chrome with the header logo in
  // loader-mode the entire time. The body is suppressed while booting.
  // When data settles, the header logo transitions to emotion-mode
  // AND the body fills in — one coordinated beat.
  const isAutoRedirectingToMatch =
    entryPointReworkEnabled &&
    !hasAutoRedirectedRef.current &&
    !wasSignedOut &&
    (
      !user ||
      (
        user.isAnonymous &&
        !matchesLoading &&
        matchesLoadedForUid === user.uid &&
        matches.length === 0
      )
    );
  // The header logo flaps continuously while ANY of these are true.
  // Body suppression below uses the booting derived value.
  const isBooting = loading || isAutoRedirectingToMatch;

  // Logo color tiebreak (#95). The emotion engine's getMostUsedColor
  // can't infer "my team" when all team names are placeholders
  // (default "Team A"/"Team B") — it falls back to iterating
  // teamA first, deterministically picking the red default color,
  // which reads as a "wrong emotion" for anon-created matches.
  // Until a user customizes at least one team name, we keep the
  // logo in loader mode rather than show the wrong emotion.
  const hasInferableTeam = useMemo(() => {
    if (matches.length === 0) return false;
    const isPlaceholder = (raw) => {
      const name = (raw || '').toString().trim();
      return !name || /^Team [A-Z]$/i.test(name) || /^Team \d+$/i.test(name);
    };
    return matches.some((m) => {
      const aName = typeof m.teamA === 'object' ? m.teamA?.name || m.teamA?.teamName : m.teamA;
      const bName = typeof m.teamB === 'object' ? m.teamB?.name || m.teamB?.teamName : m.teamB;
      return !isPlaceholder(aName) || !isPlaceholder(bName);
    });
  }, [matches]);

  const isLogoLoading = isBooting || matchesLoading || (matches.length > 0 && !hasInferableTeam);

  // 🔹 Zero state if not logged in — only when we're past loading
  // AND not about to auto-redirect into a new match. Otherwise the
  // chrome-with-empty-body path in the main render handles the visual.
  if (!loading && !user && !isAutoRedirectingToMatch) {
    return (
      <div className="landing zero-state">
        <div className="match-header" style={{ marginTop: '2rem' }}>
          <div style={{ marginBottom: '2rem' }}>
            <DynamicFlapperLogo matches={[]} size="medium" showTooltip={true} />
          </div>
          <h1 className="header--hero">What's The Score?</h1>
          <p>Track and share volleyball scores with ease.</p>
        </div>
        
        {hasGuestMatches && (
          <div className="guest-matches-callout">
            <p className="guest-matches-text">
              You've viewed {getGuestMatchCount()} match{getGuestMatchCount() !== 1 ? 'es' : ''}! Sign in to save your history
            </p>
          </div>
        )}

        {/* Entry-point rework: primary CTA is "Start a match" (no signup
            required — anon Firebase user is created on tap). Sign-in is
            demoted to a secondary toggle that reveals the existing form. */}
        {entryPointReworkEnabled && (
          <div className="entry-cta">
            <button
              className="btn-start entry-cta-primary"
              onClick={handleCreateMatch}
              type="button"
            >
              Start a match
            </button>
            <button
              className="auth-link entry-cta-secondary"
              type="button"
              onClick={() => setShowAuthForm((v) => !v)}
            >
              {showAuthForm
                ? 'Hide sign-in'
                : 'Sign in to save your history across devices'}
            </button>
          </div>
        )}

        {/* Legacy auth wall — hidden when entry-point rework is on,
            UNLESS the user explicitly toggled "Sign in to save". */}
        {(!entryPointReworkEnabled || showAuthForm) && (
        <div className="auth-buttons">
          {/* Google */}
          <button 
            className="auth-btn google-btn" 
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google logo"
            />
            Sign in with Google
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          {/* Password Reset Modal */}
          {showPasswordReset ? (
            <form className="auth-email" onSubmit={handlePasswordReset}>
              <h3>Reset Password</h3>
              <p className="auth-hint">Enter your email to receive a password reset link.</p>
              <input
                type="email"
                placeholder="Email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                disabled={isLoading}
              />
              <button 
                className="auth-btn email-btn" 
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button 
                className="auth-link" 
                type="button"
                onClick={() => setShowPasswordReset(false)}
              >
                Back to Sign In
              </button>
            </form>
          ) : (
            <form className="auth-email" onSubmit={handleEmailAuth}>
              <h3>{isSignUp ? 'Create Account' : 'Sign In'}</h3>
              
              {isSignUp && (
                <input
                  type="text"
                  placeholder="Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={isLoading}
                />
              )}
              
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
              
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />

              <button 
                className="auth-btn email-btn" 
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
              </button>

              {!isSignUp && (
                <button 
                  className="auth-link" 
                  type="button"
                  onClick={() => setShowPasswordReset(true)}
                >
                  Forgot password?
                </button>
              )}

              <button
                className="auth-link"
                type="button"
                onClick={() => setIsSignUp(!isSignUp)}
              >
                {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </button>
            </form>
          )}
        </div>
        )}

        <div className="support-cta zero-support">
          <p className="support-cta-text">
            What's The Score? exists because gym scoreboards are hard to see and everyone keeps asking, "What's the score?"
          </p>
          <p className="support-cta-text">
            If it makes your day easier, you can buy me a coffee.
          </p>
          <a
            className="support-button support-cta-button"
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Coffee size={16} weight="fill" />
            Buy me a coffee
          </a>
        </div>

      </div>
    );
  }

  // 🔹 Normal state if logged in
  return (
    <div className={`landing ${matches.length > 0 ? 'has-matches' : ''}`}>
      {/* Drawer Navigation */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSignIn={() => setShowSavePromptSheet(true)}
      />
      
      <div className="match-header">
        <button 
          className="menu-btn"
          onClick={() => setIsDrawerOpen(true)}
          aria-label="Menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256">
            <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z"></path>
          </svg>
        </button>
        
        <div className="header-logo-wrapper">
          {isLogoLoading ? (
            <DynamicFlapperLogo
              matches={[]}
              size="small"
              isLoading={true}
              showTooltip={false}
            />
          ) : (
            <DynamicFlapperLogo
              matches={matches}
              size="small"
              showTooltip={true}
            />
          )}
        </div>
        <button
          type="button"
          className="support-icon-btn"
          onClick={() => navigate('/upgrade')}
          aria-label="Support What's The Score?"
        >
          <Coffee size={20} weight="fill" />
        </button>
      </div>

      {/* Loading state is communicated by the header logo flapper.
          Body suppresses across all booting states (loading,
          auto-redirecting, matches-loading). When ready, header
          logo transitions from loader to emotion AND the body
          fills in — one coordinated beat instead of multiple
          inconsistent flashes. */}
      {!isBooting && !matchesLoading && (
        <div className="match-content">
          {geoBannerEnabled && <GeoBanner currentUserUid={user?.uid} />}
          {showSavePromptBanner && (
            <div className="save-prompt-banner">
              <div className="save-prompt-banner-text">
                Save your {matches.length} match{matches.length !== 1 ? 'es' : ''} across devices
              </div>
              <div className="save-prompt-banner-actions">
                <button
                  type="button"
                  className="save-prompt-banner-cta"
                  onClick={() => setShowSavePromptSheet(true)}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className="save-prompt-banner-dismiss"
                  onClick={() => {
                    sessionStorage.setItem('savePromptDismissed', '1');
                    setSavePromptDismissed(true);
                  }}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          {matches.length === 0 ? (
            <p className="match-zero">To get started, create a new match.</p>
          ) : (
            <div className="match-list">
              {groupMatchesWithSharedLabels(visibleMatches, sharedGroupLabels, customLabels).map((group) => (
              <MatchGroup
                key={group.dateKey}
                label={group.label}
                dateKey={group.originalDateKey || group.dateKey}
                matches={group.matches}
                defaultExpanded={groupCollapseStates[group.dateKey] !== false}
                onLabelChange={handleLabelChange}
                onDeleteGroup={handleDeleteGroup}
                onCollapseStateChange={handleCollapseStateChange}
                onShareGroup={handleShareGroup}
                isPro={hasFullAccess}
                user={user}
                renderMatch={(match) => {
              const { id, teamA, teamB, sets = [], score = {}, setsWon = {}, status: storedStatus } = match;

              const teamASetWins = setsWon?.teamA || 0;
              const teamBSetWins = setsWon?.teamB || 0;

              // Calculate status dynamically based on current match state
              const dynamicStatus = getMatchStatus(setsWon, sets, true);
              
              // Use stored status if available, otherwise use dynamic calculation
              // This preserves the original logic but allows real-time updates
              const status = storedStatus || dynamicStatus;

              let leader = null;
              if (teamASetWins > teamBSetWins) {
                leader = 'teamA';
              } else if (teamBSetWins > teamASetWins) {
                leader = 'teamB';
              } else if (score?.teamA > score?.teamB) {
                leader = 'teamA';
              } else if (score?.teamB > score?.teamA) {
                leader = 'teamB';
              }

              let bgColor = '#999';
              if (leader === 'teamA') {
                bgColor = teamA?.color || '#ffcc00';
              } else if (leader === 'teamB') {
                bgColor = teamB?.color || '#0066ff';
              }

              const textColor = getContrastTextColor(bgColor);

              const isOpenThis = swipeOpenId === id;
              // Resting transform — applied by React. During an active
              // drag we override this imperatively via the DOM (see
              // onTouchMove) to avoid re-render jank.
              const restOffset = isOpenThis ? -REVEAL_WIDTH : 0;

              return (
                <div className="row-swipe-container" key={id}>
                  {/* Red delete action revealed at the right edge on swipe.
                      Using a div instead of <button> to eliminate every
                      possible iOS Safari native button quirk (extra min-height,
                      implicit shadows, etc.). Click semantics via role/onClick. */}
                  <div
                    className="swipe-delete-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(e, id, match.isOwner);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={match.isOwner ? "Delete match" : "Remove match from list"}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleDelete(e, id, match.isOwner);
                      }
                    }}
                  >
                    <Trash size={22} weight="bold" color="#ffffff" />
                  </div>
                <div
                  className={`row ${textColor === '#ffffff' ? 'dark-bg' : 'light-bg'}`}
                  onTouchStart={(e) => {
                    // On wider screens the inline trash button is visible
                    // (no swipe needed), so don't initiate a drag.
                    if (window.matchMedia('(min-width: 768px)').matches) return;
                    const touch = e.touches[0];
                    const rowEl = e.currentTarget;
                    const startOffset = isOpenThis ? -REVEAL_WIDTH : 0;
                    dragRef.current = {
                      id,
                      startX: touch.clientX,
                      hasMoved: false,
                      rowWidth: rowEl.offsetWidth,
                      startOffset,
                      currentOffset: startOffset,
                      rowEl,
                    };
                    // Kill the transition while finger is down so the row
                    // tracks 1:1 with the touch — no interpolation lag.
                    rowEl.style.transition = 'none';
                  }}
                  onTouchMove={(e) => {
                    const drag = dragRef.current;
                    if (!drag || drag.id !== id) return;
                    const touch = e.touches[0];
                    const delta = touch.clientX - drag.startX;
                    if (Math.abs(delta) > 5) drag.hasMoved = true;
                    // Allow the row to slide all the way off-screen during
                    // the drag (visually clipped by the page-level overflow).
                    const next = Math.max(
                      -drag.rowWidth,
                      Math.min(0, drag.startOffset + delta)
                    );
                    drag.currentOffset = next;
                    // Coalesce to one DOM write per animation frame — touchmove
                    // can fire faster than the display refreshes (esp. 120Hz
                    // devices), and queuing redundant writes causes jank.
                    if (drag.rafId == null) {
                      drag.rafId = requestAnimationFrame(() => {
                        drag.rafId = null;
                        // translate3d forces a dedicated GPU layer so the
                        // row (with its big box-shadow) composites instead
                        // of repainting every frame.
                        drag.rowEl.style.transform = `translate3d(${drag.currentOffset}px, 0, 0)`;
                      });
                    }
                  }}
                  onTouchEnd={() => {
                    const drag = dragRef.current;
                    if (!drag || drag.id !== id) return;
                    if (drag.rafId != null) cancelAnimationFrame(drag.rafId);
                    const finalOffset = drag.currentOffset;
                    const hadMoved = drag.hasMoved;
                    // Restore React-managed transition for the snap animation.
                    drag.rowEl.style.transition = '';
                    drag.rowEl.style.transform = '';
                    dragRef.current = null;
                    // Past threshold → snap open at -REVEAL_WIDTH so the
                    // user can tap the red delete button to confirm.
                    if (finalOffset < -SNAP_THRESHOLD) {
                      setSwipeOpenId(id);
                    } else if (hadMoved) {
                      setSwipeOpenId((cur) => (cur === id ? null : cur));
                    }
                  }}
                  onClick={() => {
                    // A tap on an open row closes it (does not navigate).
                    if (isOpenThis) {
                      setSwipeOpenId(null);
                      return;
                    }
                    // Suppress tap that followed a meaningful swipe.
                    if (dragRef.current && dragRef.current.hasMoved) {
                      return;
                    }
                    // Check if current user is the creator
                    const isCreator = match.creatorId && match.creatorId === user?.uid;

                    // Navigate to editor if creator, otherwise viewer
                    if (isCreator) {
                      navigate(`/match/editor/${id}`);
                    } else {
                      navigate(`/match/${id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') navigate(`/match/${id}`);
                  }}
                  style={{
                    backgroundColor: bgColor,
                    color: textColor,
                    transform: `translate3d(${restOffset}px, 0, 0)`,
                    transition: 'transform 220ms ease',
                    touchAction: 'pan-y',
                  }}
                >
                  <div className="team-names">
                    <div className="team-name" style={{ opacity: leader === 'teamB' ? 0.65 : 1 }}>
                      <span style={{ width: '1.5rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: '0.4rem' }}>
                        {leader === 'teamA' && (
                          <Check size={16} weight="bold" color={textColor} />
                        )}
                      </span>
                      <span className="team-name-text">{teamA?.name || 'Team A'}</span>
                    </div>
                    <div className="team-name" style={{ opacity: leader === 'teamA' ? 0.65 : 1 }}>
                      <span style={{ width: '1.5rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: '0.4rem' }}>
                        {leader === 'teamB' && (
                          <Check size={16} weight="bold" color={textColor} />
                        )}
                      </span>
                      <span className="team-name-text">{teamB?.name || 'Team B'}</span>
                    </div>
                  </div>

                  <SetDisplay sets={sets} leader={leader} />

                  <div className="match-status-wrapper">
                    <div 
                      className={`match-status match-status--${status === 'final' ? 'final' : 'playing'}`}
                      style={{ 
                        color: status === 'final' ? textColor : bgColor 
                      }}
                    >
                      {status === 'final' ? 'FINAL' : 'LIVE'}
                    </div>
                  </div>

                  {/* 🔹 Delete/Remove button — for everyone */}
                  <button
                    onClick={(e) => handleDelete(e, id, match.isOwner)}
                    className="delete-btn"
                    aria-label={match.isOwner ? "Delete match" : "Remove match from list"}
                    title={match.isOwner ? "Delete match" : "Remove from list"}
                  >
                      <Trash size={16} weight="regular" color={textColor} />
                  </button>
                </div>
                </div>
              );
                }}
              />
            ))}
          </div>
        )}

        {/* Single CTA Button */}
        <button className="btn-start" onClick={handleCreateMatch}>
          {paymentsEnabled && !isPro ? (
            <>
              <Sparkle size={20} weight="fill" style={{ marginRight: '8px', color: '#FFD700' }} />
              Upgrade to Start Matches
            </>
          ) : 'Start a New Match'}
        </button>
        </div>
      )}
      
      {/* Share Group Modal */}
      {shareGroupModal && (
        <ShareGroupModal
          groupData={shareGroupModal}
          onClose={() => setShareGroupModal(null)}
        />
      )}

      <SignInSheet
        open={showSavePromptSheet}
        onClose={() => setShowSavePromptSheet(false)}
        title="Save your match history"
        subtitle={
          matches.length > 0
            ? `Sign in to keep your ${matches.length} match${matches.length !== 1 ? 'es' : ''} across devices.`
            : 'Sign in to keep your matches across devices.'
        }
      />
    </div>
  );
}

export default LandingPage;
